import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  IconButton,
  Alert,
  Collapse,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { AccessTime, CalendarMonth, InfoOutlined, ExpandMore, ExpandLess, NightsStay } from "@mui/icons-material";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";

import CustomInput from "../../components/CustomInput";
import TimezoneConfig from "../../components/TimezoneConfig";
import { isNightShift } from "../../utils/calendarUtils";
import { useSystemTheme } from "context/SystemThemeContext";

const GoogleCalendarConfig = ({ domainId, onConfigSaved, initialStatus }) => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [testing, setTesting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [timezonesLoaded, setTimezonesLoaded] = useState(false);
  const [isEditingTimezone, setIsEditingTimezone] = useState(false);
  const [currentTimezone, setCurrentTimezone] = useState('UTC');
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [showMaxDaysField, setShowMaxDaysField] = useState(false);
  const [showMinNoticeField, setShowMinNoticeField] = useState(false);
  
  const defaultConfig = {
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    slot_duration_minutes: 30,
    min_lead_time_hours: 4,
    max_booking_days: 60,
    buffer_before_minutes: 5,
    buffer_after_minutes: 5,
    timezone: 'UTC',
    enabled: true
  };
  
  useEffect(() => {
    const loadTimezones = () => {
      if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
        try {
          const supportedTimezones = Intl.supportedValuesOf("timeZone");
          const allTimezones = ['UTC', ...supportedTimezones.filter(tz => tz !== 'UTC')];
          setTimezones(allTimezones);
          setTimezonesLoaded(true);
        } catch (err) {
          console.error("Failed to get supported timezones:", err);
          setFallbackTimezones();
        }
      } else {
        setFallbackTimezones();
      }
    };

    const setFallbackTimezones = () => {
      const fallbackTimezones = [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Toronto',
        'America/Vancouver',
        'America/Sao_Paulo',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Madrid',
        'Europe/Rome',
        'Europe/Amsterdam',
        'Asia/Tokyo',
        'Asia/Shanghai',
        'Asia/Singapore',
        'Asia/Hong_Kong',
        'Asia/Seoul',
        'Asia/Dubai',
        'Australia/Sydney',
        'Australia/Melbourne',
        'Pacific/Auckland',
        'Africa/Cairo',
        'Africa/Johannesburg',
      ];
      setTimezones(fallbackTimezones);
      setTimezonesLoaded(true);
    };

    loadTimezones();
  }, []);
  
  useEffect(() => {
    if (domainId) {
      loadConfig();
      fetchTimezone();
    }
  }, [domainId]);
  
  const fetchTimezone = async () => {
    if (!domainId) return;
    
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/domain/timezone`, {
        headers: { "DOMAIN-TOKEN": domainId }
      });
      
      if (res.data.success) {
        const timezone = res.data.data.timezone || 'UTC';
        setCurrentTimezone(timezone);
        if (config) {
          setConfig({...config, timezone});
        }
      }
    } catch (err) {
      console.error("Failed to fetch timezone:", err);
    }
  };
  
  const loadConfig = async () => {
    setLoading(true);
    try {
      if (initialStatus && initialStatus.config) {
        const loadedConfig = initialStatus.config;
        setConfig(loadedConfig);
        setOriginalConfig(loadedConfig);
        setCurrentTimezone(initialStatus.timezone || 'UTC');
      } else {
        const response = await axios.get(
          `${FAST_API_BASE_URL}/api/single-calendar/config`,
          { headers: { "DOMAIN-TOKEN": domainId } }
        );
        
        if (response.data.success) {
          const loadedConfig = response.data.data.config;
          setConfig(loadedConfig);
          setOriginalConfig(loadedConfig);
          setCurrentTimezone(response.data.data.timezone || 'UTC');
        } else {
          await fetchTimezone();
          const loadedConfig = {...defaultConfig, timezone: currentTimezone};
          setConfig(loadedConfig);
          setOriginalConfig(loadedConfig);
        }
      }
    } catch (error) {
      console.error("Failed to load config:", error);
      const loadedConfig = {...defaultConfig, timezone: 'UTC'};
      setConfig(loadedConfig);
      setOriginalConfig(loadedConfig);
    } finally {
      setLoading(false);
    }
  };
  
  const saveTimezone = async (timezone) => {
    if (!domainId || !timezone) return;
    
    setSavingTimezone(true);
    try {
      const res = await axios.patch(
        `${FAST_API_BASE_URL}/api/domain/timezone`,
        { timezone: timezone },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setCurrentTimezone(timezone);
        setConfig({...config, timezone});
        setOriginalConfig({...originalConfig, timezone});
      }
    } catch (err) {
      console.error("Failed to update timezone:", err);
      throw err;
    } finally {
      setSavingTimezone(false);
    }
  };
  
  const saveConfig = async () => {
    if (!config) return;
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/single-calendar/configure`,
        config,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      if (response.data.success) {
        setOriginalConfig(config);
        if (onConfigSaved) onConfigSaved(config);
      }
    } catch (error) {
      console.error("Failed to save config:", error);
    } finally {
      setSaving(false);
    }
  };
  
  const toggleDay = (day) => {
    const days = [...config.working_days];
    if (days.includes(day)) {
      setConfig({...config, working_days: days.filter(d => d !== day)});
    } else {
      setConfig({...config, working_days: [...days, day]});
    }
  };
  
  const isNightShiftFlag = isNightShift(config?.working_hours_start, config?.working_hours_end);
  
  const hasChanges = useMemo(() => {
    if (!config || !originalConfig) return false;
    
    return JSON.stringify(config) !== JSON.stringify(originalConfig);
  }, [config, originalConfig]);
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress sx={{ color: '#219297' }} />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Timezone Configuration */}
      <TimezoneConfig 
        timezone={config?.timezone || 'UTC'}
        onTimezoneChange={(tz) => setConfig({...config, timezone: tz})}
        timezones={timezones}
        timezonesLoaded={timezonesLoaded}
        currentTimezone={currentTimezone}
        isEditing={isEditingTimezone}
        setIsEditing={setIsEditingTimezone}
        saving={saving || savingTimezone}
        domainId={domainId}
        onTimezoneSave={saveTimezone}
      />
      
      {/* Working Hours Configuration */}
      <Paper sx={{ 
        p: 2.5, 
        mb: 3,
        borderRadius: '12px',
        backgroundColor: isDarkMode ? '#0a1020' : '#f8fafa',
        border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0f0f0'}`,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}>
        <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#219297', mb: 2 }}>
          Working Hours
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                Start Time
              </Typography>
              <CustomInput
                type="time"
                value={config?.working_hours_start || '09:00'}
                onChange={(e) => setConfig({...config, working_hours_start: e.target.value})}
                startIcon={<AccessTime sx={{ color: '#219297' }} />}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                End Time
              </Typography>
              <CustomInput
                type="time"
                value={config?.working_hours_end || '17:00'}
                onChange={(e) => setConfig({...config, working_hours_end: e.target.value})}
                startIcon={<AccessTime sx={{ color: '#219297' }} />}
              />
            </Box>
          </Grid>
          
          {isNightShiftFlag && (
            <Grid item xs={12}>
              <Alert 
                severity="info" 
                icon={<NightsStay sx={{ color: '#4527a0' }} />}
                sx={{ 
                  backgroundColor: isDarkMode ? '#2d2d3f' : '#f3e5f5',
                  borderColor: isDarkMode ? '#4a5568' : '#ba68c8',
                  color: isDarkMode ? '#e2e8f0' : '#4a148c',
                  fontSize: '0.75rem',
                  borderRadius: '8px',
                  '& .MuiAlert-icon': {
                    color: '#4527a0'
                  }
                }}
              >
                <Typography variant="caption" fontWeight="500">
                  Night shift detected. Working hours span across midnight.
                </Typography>
              </Alert>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
              Working Days
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                <Chip
                  key={day}
                  label={day.slice(0, 3).toUpperCase()}
                  size="small"
                  onClick={() => toggleDay(day)}
                  sx={{
                    fontSize: '0.7rem',
                    height: '28px',
                    cursor: 'pointer',
                    backgroundColor: config?.working_days?.includes(day) 
                      ? '#219297' 
                      : (isDarkMode ? '#060C18' : '#ffffff'),
                    color: config?.working_days?.includes(day) 
                      ? '#ffffff' 
                      : (isDarkMode ? '#e2e8f0' : '#666'),
                    border: `1px solid ${config?.working_days?.includes(day) 
                      ? '#219297' 
                      : (isDarkMode ? '#4a5568' : '#e0e0e0')}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: config?.working_days?.includes(day) 
                        ? '#197f85' 
                        : (isDarkMode ? '#2d2d3f' : '#f0f7fa'),
                      borderColor: config?.working_days?.includes(day) 
                        ? '#197f85' 
                        : '#219297',
                    }
                  }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ 
        p: 2.5, 
        mb: 2,
        borderRadius: '12px',
        backgroundColor: isDarkMode ? '#0a1020' : '#f8fafa',
        border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0f0f0'}`,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#219297' }}>
            Advanced Settings
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setShowAdvanced(!showAdvanced)}
            sx={{ 
              color: '#219297',
              '&:hover': {
                backgroundColor: isDarkMode ? '#2d2d3f' : '#e0f7fa'
              }
            }}
          >
            {showAdvanced ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        
        <Collapse in={showAdvanced}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', mb: 1, display: 'block', fontWeight: 500 }}>
                  Slot Duration (minutes)
                </Typography>
                <CustomInput
                  type="number"
                  value={config?.slot_duration_minutes || 30}
                  onChange={(e) => setConfig({...config, slot_duration_minutes: parseInt(e.target.value) || 30})}
                  startIcon={<AccessTime sx={{ color: '#219297' }} />}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ 
                mb: 2, 
                p: 2, 
                backgroundColor: isDarkMode ? '#060C18' : '#ffffff', 
                borderRadius: '8px', 
                border: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0f0f0'}`,
                transition: 'background-color 0.3s ease, border-color 0.3s ease',
              }}>
                <Box sx={{ mb: 3 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CalendarMonth sx={{ color: '#219297', fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', fontWeight: 500 }}>
                      Booking Window
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#e2e8f0' : '#333' }}>
                      Invitees can schedule
                    </Typography>
                    <Box
                      onClick={() => setShowMaxDaysField(!showMaxDaysField)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        padding: '4px 12px',
                        backgroundColor: isDarkMode ? '#2d2d3f' : '#ffffff',
                        border: `1px solid ${isDarkMode ? '#4a5568' : '#e0e0e0'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#219297',
                          backgroundColor: isDarkMode ? '#3d3d4f' : '#f0f7fa',
                        }
                      }}
                    >
                      <Typography variant="body2" fontWeight="600" sx={{ color: '#219297' }}>
                        {config?.max_booking_days || 60} days
                      </Typography>
                      <ExpandMore sx={{ color: '#219297', fontSize: 18 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#e2e8f0' : '#333' }}>
                      into the future
                    </Typography>
                  </Box>
                  
                  <Collapse in={showMaxDaysField}>
                    <Box sx={{ mt: 1.5 }}>
                      <CustomInput
                        type="number"
                        value={config?.max_booking_days || 60}
                        onChange={(e) => setConfig({...config, max_booking_days: parseInt(e.target.value) || 60})}
                        placeholder="Enter maximum booking days"
                        startIcon={<CalendarMonth sx={{ color: '#219297' }} />}
                      />
                    </Box>
                  </Collapse>
                </Box>
                
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AccessTime sx={{ color: '#219297', fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666', fontWeight: 500 }}>
                      Minimum Notice Time
                    </Typography>
                  </Box>
                  
                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#e2e8f0' : '#333' }}>
                      with at least
                    </Typography>
                    <Box
                      onClick={() => setShowMinNoticeField(!showMinNoticeField)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        padding: '4px 12px',
                        backgroundColor: isDarkMode ? '#2d2d3f' : '#ffffff',
                        border: `1px solid ${isDarkMode ? '#4a5568' : '#e0e0e0'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#219297',
                          backgroundColor: isDarkMode ? '#3d3d4f' : '#f0f7fa',
                        }
                      }}
                    >
                      <Typography variant="body2" fontWeight="600" sx={{ color: '#219297' }}>
                        {config?.min_lead_time_hours || 4} hours
                      </Typography>
                      <ExpandMore sx={{ color: '#219297', fontSize: 18 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#e2e8f0' : '#333' }}>
                      notice
                    </Typography>
                  </Box>
                  
                  <Collapse in={showMinNoticeField}>
                    <Box sx={{ mt: 1.5 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={config?.min_lead_time_hours || 4}
                          onChange={(e) => setConfig({...config, min_lead_time_hours: parseInt(e.target.value)})}
                          sx={{
                            backgroundColor: isDarkMode ? '#2d2d3f' : '#ffffff',
                            borderRadius: '8px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e0e0e0'}`,
                            color: isDarkMode ? '#e2e8f0' : '#333',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: isDarkMode ? '#4a5568' : '#e0e0e0'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#219297'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#219297',
                              borderWidth: '1px'
                            }
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: '8px',
                                marginTop: '4px',
                                backgroundColor: isDarkMode ? '#060C18' : '#fff',
                                boxShadow: isDarkMode ? '0px 4px 16px rgba(0,0,0,0.3)' : '0px 4px 16px rgba(0,0,0,0.08)',
                              },
                            },
                          }}
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8].map((hour) => (
                            <MenuItem 
                              key={hour} 
                              value={hour}
                              sx={{
                                fontSize: '0.9rem',
                                color: isDarkMode ? '#e2e8f0' : '#333',
                                '&:hover': {
                                  backgroundColor: isDarkMode ? '#2d2d3f' : '#f0f7fa'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#21929710',
                                  color: '#219297',
                                  fontWeight: 500
                                }
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <AccessTime sx={{ fontSize: '1rem', color: '#219297' }} />
                                <Typography variant="body2">
                                  {hour} {hour === 1 ? 'hour' : 'hours'}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: isDarkMode ? '#0d1425' : '#e3f2fd', 
                borderRadius: '8px',
                border: `1px solid ${isDarkMode ? '#2d2d3f' : '#bbdefb'}`,
                transition: 'background-color 0.3s ease, border-color 0.3s ease',
              }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <InfoOutlined sx={{ color: '#1565c0', fontSize: '1rem' }} />
                  <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600 }}>
                    Booking Window Summary
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#0d47a1', lineHeight: 1.5 }}>
                  Invitees can schedule <Box component="span" sx={{ fontWeight: 600, color: '#219297' }}>{config?.max_booking_days || 60} days</Box> into the future 
                  with at least <Box component="span" sx={{ fontWeight: 600, color: '#219297' }}>{config?.min_lead_time_hours || 4} hours</Box> notice.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Save Button and Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {config?.enabled && (
            <Chip
              label="Active"
              size="small"
              sx={{ 
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                fontWeight: 500,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          )}
          <Typography variant="caption" sx={{ color: isDarkMode ? '#a0aec0' : '#666' }}>
            {hasChanges ? 'You have unsaved changes' : 'All changes saved'}
          </Typography>
        </Box>
        
        {hasChanges && (
          <Button
            variant="contained"
            onClick={saveConfig}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : null}
            sx={{
              backgroundColor: '#219297',
              color: '#ffffff',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: '#197f85',
                boxShadow: '0 4px 12px rgba(33, 146, 151, 0.2)'
              },
              '&.Mui-disabled': {
                backgroundColor: isDarkMode ? '#2d2d3f' : '#e0e0e0',
                color: isDarkMode ? '#a0aec0' : '#999'
              }
            }}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default GoogleCalendarConfig;